import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowDownRight } from "lucide-react";
import CVReview from "@/components/CVReview";
import Services from "@/components/ServicesSummary";
import Consultation from "@/components/LeadForm";
import Footer from "@/components/Footer";
import { FadeUp, scrollToId } from "@/components/motion";
import Seo from "@/lib/seo";
import { captureUTM, track } from "@/lib/analytics";

const FAQS = [
  { q: "Is the CV Career Brand Score really free?", a: "Yes. Upload or paste your CV and see your score instantly — no payment required. You can then request the full report by email." },
  { q: "Do you use AI?", a: "AI supports the assessment, but George reviews and shapes recommendations for paid work. You remain the source of truth about your experience." },
  { q: "Who do you work with?", a: "Experienced professionals, career changers, graduates and international jobseekers building an Australian-ready CV and LinkedIn presence." },
  { q: "What does it cost to work with George?", a: "Pricing depends on what you need, so we do not publish fixed prices. Contact us to discuss your needs. We will explain the scope and provide a clear quote before you decide." },
];

export default function LinkedInLanding() {
  useEffect(() => {
    window.scrollTo(0, 0);
    captureUTM();
    track("linkedin_landing_view");
  }, []);

  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  };

  return (
    <main className="bg-[#0A0A0A] text-[#F8F8F5]">
      <Seo
        title="Free CV Career Brand Score — Barrett Career Brand"
        description="Your experience has value. Get a free CV Career Brand Score and practical next steps, or request a free 15-minute consultation with George Barrett."
        path="/linkedin"
        jsonLd={faqJsonLd}
      />

      <header className="border-b border-[#27272A]">
        <div className="max-w-[1200px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display text-lg font-bold tracking-tighter">
            Barrett Career <span className="text-[#DFFF00]">Brand</span>
          </Link>
        </div>
      </header>

      {/* Concise mobile-first hero */}
      <section className="max-w-[1200px] mx-auto px-6 pt-16 pb-12 md:pt-24">
        <FadeUp>
          <p className="tracking-label text-[11px] uppercase font-body font-bold text-[#DFFF00] mb-6">Welcome from LinkedIn</p>
          <h1 className="font-display font-bold tracking-tighter leading-[1.0] text-4xl sm:text-5xl lg:text-6xl">
            Your experience has value. Let's make sure employers can <span className="font-serif-accent font-light">see it.</span>
          </h1>
          <p className="mt-6 font-body text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
            I'm George Barrett. I help professionals turn a strong career into a CV and LinkedIn profile that
            actually get the callback — combining human judgement with AI-supported analysis.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              data-testid="li-primary-cta"
              onClick={() => scrollToId("#cv-review")}
              className="rounded-full bg-[#DFFF00] text-[#0A0A0A] font-body font-semibold px-7 py-4 inline-flex items-center justify-center gap-2 hover:bg-[#C8E600] transition-colors"
            >
              Get your free CV Career Brand Score <ArrowDownRight size={18} />
            </button>
            <button
              data-testid="li-secondary-cta"
              onClick={() => scrollToId("#consultation")}
              className="rounded-full border border-[#27272A] text-[#F8F8F5] font-body px-7 py-4 hover:border-[#DFFF00] transition-colors"
            >
              Request a free 15-minute consultation
            </button>
          </div>
        </FadeUp>
      </section>

      <CVReview />
      <Services />

      {/* FAQ */}
      <section data-testid="faq" id="faq" className="bg-[#141414] border-y border-[#27272A] py-24">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="font-display font-bold tracking-tighter text-3xl md:text-5xl mb-10">Questions, answered.</h2>
          <div className="divide-y divide-[#27272A] border-t border-[#27272A]">
            {FAQS.map((f, i) => (
              <FadeUp key={i}>
                <div className="py-6">
                  <h3 className="font-display text-lg font-bold text-[#F8F8F5] mb-2">{f.q}</h3>
                  <p className="font-body text-[#A1A1AA] leading-relaxed">{f.a}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <Consultation />
      <Footer />
    </main>
  );
}
